import React from 'react';
import styled from 'styled-components';
import { ADHERENT_OPTIONS, PLACE_DU_DESIGN_OPTIONS, PROFIL_CREATEUR_OPTIONS } from '../sdeConstants';

const StepContainer = styled.div`max-width: 700px; margin: 0 auto;`;
const StepTitle = styled.h2`font-size: 1.5rem; font-weight: 600; color: #1F2937; margin-bottom: 0.5rem;`;
const StepSubtitle = styled.p`color: #6B7280; margin-bottom: 2rem; font-size: 0.95rem;`;

const ReviewSection = styled.div`
  background: white; border: 1px solid #E5E7EB; border-left: 4px solid #2563EB;
  border-radius: 0 12px 12px 0; padding: 1.25rem 1.5rem; margin-bottom: 1rem;
  h4 { color: #2563EB; font-size: 1rem; margin: 0 0 0.75rem; }
`;

const ReviewRow = styled.div`
  display: flex; justify-content: space-between; padding: 0.4rem 0; border-bottom: 1px solid #F3F4F6;
  &:last-child { border: none; }
  span:first-child { color: #6B7280; font-size: 0.9rem; }
  span:last-child { color: #1F2937; font-weight: 500; font-size: 0.9rem; text-align: right; max-width: 60%; }
`;

const ReviewBlock = styled.div`
  padding: 0.4rem 0; border-bottom: 1px solid #F3F4F6;
  &:last-child { border: none; }
  .label { color: #6B7280; font-size: 0.9rem; margin-bottom: 0.25rem; }
  .value { color: #1F2937; font-size: 0.9rem; white-space: pre-wrap; }
  .value a { color: #2563EB; text-decoration: underline; }
`;

const TagList = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.25rem;
`;
const Tag = styled.span`
  display: inline-block; padding: 0.2rem 0.6rem; background: #DBEAFE; color: #1E40AF;
  border-radius: 6px; font-size: 0.8rem; font-weight: 500;
`;

const SubmitButton = styled.button`
  width: 100%; padding: 1rem; margin-top: 1.5rem;
  background: linear-gradient(135deg, #2563EB, #1D4ED8); color: white;
  border: none; border-radius: 12px; font-size: 1.1rem; font-weight: 600;
  cursor: pointer; transition: all 0.3s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(37, 99, 235, 0.35); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const WarningBox = styled.div`
  background: #FEF3C7; border: 1px solid #FCD34D; border-radius: 8px; padding: 1rem;
  margin-bottom: 1rem; color: #92400E; font-size: 0.9rem;
`;

const SuccessBox = styled.div`
  background: #D1FAE5; border: 1px solid #6EE7B7; border-radius: 12px; padding: 2rem;
  text-align: center; margin-top: 1rem;
  h3 { color: #065F46; margin: 0.5rem 0; }
  p { color: #047857; }
`;

const ProjectReview = styled.div`
  background: #F9FAFB; border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 0.5rem;
  .proj-title { font-weight: 600; color: #374151; font-size: 0.9rem; margin-bottom: 0.25rem; }
  .proj-detail { color: #6B7280; font-size: 0.85rem; }
`;

const lookupLabels = (values, options, langKey = 'label') => {
  if (!values || !Array.isArray(values) || values.length === 0) return null;
  return values.map(v => {
    const opt = options.find(o => o.value === v);
    return opt ? (opt[langKey] || opt.label || v) : v;
  });
};

const CandidaturesLink = styled.a`
  display: inline-block; margin-top: 1rem; padding: 0.75rem 2rem;
  background: #2563EB; color: white; border-radius: 10px; font-size: 1rem; font-weight: 600;
  text-decoration: none; transition: all 0.2s;
  &:hover { background: #1D4ED8; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(37,99,235,0.3); color: white; }
`;

const SharedStep_Review = ({ data, populationType, onSubmit, isSubmitting, isSubmitted, loadedStatus, lang = 'fr', eventId }) => {
  const rep1 = data.representative_1 || {};
  const rep2 = data.representative_2 || {};
  const metadata = data.metadata || {};
  const isPopA = populationType === 'pop_a';

  if (isSubmitted) {
    return (
      <StepContainer>
        <SuccessBox>
          <div style={{ fontSize: '3rem' }}>&#10003;</div>
          <h3>{lang === 'fr'
            ? (isPopA ? 'Inscription soumise avec succès !' : 'Votre profil a bien été enregistré !')
            : 'Registration submitted successfully!'}</h3>
          <p>{lang === 'fr'
            ? (isPopA
              ? 'Votre inscription a bien été enregistrée. Vous recevrez une confirmation par email.'
              : 'Vous allez recevoir une confirmation par email.')
            : 'Your registration has been recorded. You will receive a confirmation email.'}
          </p>
          {!isPopA && eventId && (
            <>
              <p style={{ marginTop: '0.75rem', fontWeight: 600 }}>
                {lang === 'fr'
                  ? 'Vous pouvez maintenant consulter les demandes des entreprises et effectuer vos demandes de rendez-vous :'
                  : 'You can now browse companies and submit your applications:'}
              </p>
              <CandidaturesLink href={`/evenements/sdo/candidatures?eid=${eventId}`}>
                {lang === 'fr' ? 'Accéder aux candidatures' : 'Browse candidatures'}
              </CandidaturesLink>
            </>
          )}
        </SuccessBox>
      </StepContainer>
    );
  }

  const alreadySubmitted = loadedStatus === 'submitted';

  const adherentLabels = lookupLabels(metadata.adherent_cotisant, ADHERENT_OPTIONS);
  const placeLabels = lookupLabels(metadata.place_du_design, PLACE_DU_DESIGN_OPTIONS, lang === 'fr' ? 'label_fr' : 'label_en');
  const formatSelectValues = (values) => {
    if (!values || !Array.isArray(values) || values.length === 0) return null;
    return values.map(v => v.label || v.value || v);
  };
  const profilLabels = lookupLabels(metadata.profil_createur, PROFIL_CREATEUR_OPTIONS, lang === 'fr' ? 'label_fr' : 'label_en');

  return (
    <StepContainer>
      <StepTitle>{lang === 'fr' ? 'Vérifiez vos informations' : 'Review your information'}</StepTitle>
      <StepSubtitle>
        {lang === 'fr' ? 'Veuillez vérifier vos informations avant de soumettre' : 'Please review your information before submitting'}
      </StepSubtitle>

      {/* Company & Contact */}
      <ReviewSection>
        <h4>{lang === 'fr' ? 'Entreprise & Contact principal' : 'Company & Primary Contact'}</h4>
        <ReviewRow><span>{lang === 'fr' ? 'Entreprise' : 'Company'}</span><span>{data.company_name || '-'}</span></ReviewRow>
        {isPopA && adherentLabels && (
          <ReviewRow>
            <span>{lang === 'fr' ? 'Adhérent / Cotisant' : 'Member'}</span>
            <span><TagList>{adherentLabels.map((l, i) => <Tag key={i}>{l}</Tag>)}</TagList></span>
          </ReviewRow>
        )}
        <ReviewRow><span>{lang === 'fr' ? 'Contact' : 'Contact'}</span><span>{rep1.firstname} {rep1.name}</span></ReviewRow>
        <ReviewRow><span>Email</span><span>{rep1.email || '-'}</span></ReviewRow>
        <ReviewRow><span>{lang === 'fr' ? 'Tél. mobile' : 'Mobile'}</span><span>{rep1.phone || '-'}</span></ReviewRow>
        {rep1.landline && <ReviewRow><span>{lang === 'fr' ? 'Tél. fixe' : 'Landline'}</span><span>{rep1.landline}</span></ReviewRow>}
        <ReviewRow><span>{lang === 'fr' ? 'Poste' : 'Job Title'}</span><span>{rep1.job_title || '-'}</span></ReviewRow>
      </ReviewSection>

      {/* Second contact */}
      {rep2.name && (
        <ReviewSection>
          <h4>{lang === 'fr' ? 'Second contact' : 'Secondary Contact'}</h4>
          <ReviewRow><span>{lang === 'fr' ? 'Contact' : 'Contact'}</span><span>{rep2.firstname} {rep2.name}</span></ReviewRow>
          {rep2.email && <ReviewRow><span>Email</span><span>{rep2.email}</span></ReviewRow>}
          {rep2.phone && <ReviewRow><span>{lang === 'fr' ? 'Tél. mobile' : 'Mobile'}</span><span>{rep2.phone}</span></ReviewRow>}
          {rep2.landline && <ReviewRow><span>{lang === 'fr' ? 'Tél. fixe' : 'Landline'}</span><span>{rep2.landline}</span></ReviewRow>}
          {rep2.job_title && <ReviewRow><span>{lang === 'fr' ? 'Poste' : 'Job Title'}</span><span>{rep2.job_title}</span></ReviewRow>}
        </ReviewSection>
      )}

      {/* Pop A: Needs */}
      {isPopA && (
        <ReviewSection>
          <h4>{lang === 'fr' ? 'Vos besoins' : 'Your Needs'}</h4>
          {metadata.offre && (
            <ReviewBlock>
              <div className="label">{lang === 'fr' ? 'Offre / Typologie' : 'Offering / Typology'}</div>
              <div className="value">{metadata.offre}</div>
            </ReviewBlock>
          )}
          {metadata.savoir_faire && (
            <ReviewBlock>
              <div className="label">{lang === 'fr' ? 'Savoir-faire, matériaux et techniques' : 'Know-how, materials and techniques'}</div>
              <div className="value">{metadata.savoir_faire}</div>
            </ReviewBlock>
          )}
          {placeLabels && (
            <ReviewRow>
              <span>{lang === 'fr' ? 'Place du design' : 'Role of design'}</span>
              <span><TagList>{placeLabels.map((l, i) => <Tag key={i}>{l}</Tag>)}</TagList></span>
            </ReviewRow>
          )}
        </ReviewSection>
      )}

      {/* Pop B: Expertise */}
      {!isPopA && (
        <ReviewSection>
          <h4>{lang === 'fr' ? 'Votre expertise' : 'Your Expertise'}</h4>
          {profilLabels && (
            <ReviewRow>
              <span>{lang === 'fr' ? 'Profil' : 'Profile'}</span>
              <span><TagList>{profilLabels.map((l, i) => <Tag key={i}>{l}</Tag>)}</TagList></span>
            </ReviewRow>
          )}
          {metadata.savoir_faire && (
            <ReviewBlock>
              <div className="label">{lang === 'fr' ? 'Savoir-faire et spécialités' : 'Know-how and specialties'}</div>
              <div className="value">{metadata.savoir_faire}</div>
            </ReviewBlock>
          )}
          {metadata.materiaux_techniques && (
            <ReviewBlock>
              <div className="label">{lang === 'fr' ? 'Matériaux et techniques' : 'Materials and techniques'}</div>
              <div className="value">{metadata.materiaux_techniques}</div>
            </ReviewBlock>
          )}
          {metadata.demarche_creative && (
            <ReviewBlock>
              <div className="label">{lang === 'fr' ? 'Démarche créative' : 'Creative approach'}</div>
              <div className="value">{metadata.demarche_creative}</div>
            </ReviewBlock>
          )}
        </ReviewSection>
      )}

      {/* Pop A: Projects */}
      {isPopA && data.projects?.length > 0 && (
        <ReviewSection>
          <h4>{lang === 'fr' ? 'Projets' : 'Projects'} ({data.projects.length})</h4>
          {data.projects.map((p, i) => {
            const profilLabels = lookupLabels(p.profil_createur, PROFIL_CREATEUR_OPTIONS, lang === 'fr' ? 'label_fr' : 'label_en');
            return (
              <ProjectReview key={i}>
                <div className="proj-title">{lang === 'fr' ? `Projet ${i + 1}` : `Project ${i + 1}`}</div>
                {p.description && <div className="proj-detail">{p.description.substring(0, 120)}{p.description.length > 120 ? '...' : ''}</div>}
                {p.typologie && <div className="proj-detail">{lang === 'fr' ? 'Typologie' : 'Typology'}: {p.typologie}</div>}
                {profilLabels && (
                  <div style={{ marginTop: 4 }}>
                    <TagList>{profilLabels.map((l, j) => <Tag key={j}>{l}</Tag>)}</TagList>
                  </div>
                )}
              </ProjectReview>
            );
          })}
        </ReviewSection>
      )}

      {/* Pop B: Portfolio */}
      {!isPopA && data.portfolio_projects?.length > 0 && (
        <ReviewSection>
          <h4>Portfolio ({data.portfolio_projects.length})</h4>
          {data.portfolio_projects.map((p, i) => (
            <ProjectReview key={i}>
              <div className="proj-title">{p.title || `Projet ${i + 1}`}</div>
              {p.description && <div className="proj-detail">{p.description.substring(0, 120)}{p.description.length > 120 ? '...' : ''}</div>}
              {p.confidentiality && <div className="proj-detail">{p.confidentiality === 'confidentiel' ? (lang === 'fr' ? 'Confidentiel' : 'Confidential') : 'Public'}</div>}
            </ProjectReview>
          ))}
        </ReviewSection>
      )}

      {/* Preferences */}
      <ReviewSection>
        <h4>{lang === 'fr' ? 'Format' : 'Format'}</h4>
        <ReviewRow>
          <span>Format</span>
          <span>{lang === 'fr' ? 'Présentiel — 29 sept. 2026 — RDV de 15 min' : 'In-person — Sept 29, 2026 — 15 min meetings'}</span>
        </ReviewRow>
        {metadata.additional_info && (
          <ReviewBlock>
            <div className="label">{lang === 'fr' ? 'Informations complémentaires' : 'Additional info'}</div>
            <div className="value">{metadata.additional_info}</div>
          </ReviewBlock>
        )}
      </ReviewSection>

      {/* Comments */}
      {metadata.comments && metadata.comments.replace(/<[^>]*>/g, '').trim() && (
        <ReviewSection>
          <h4>{lang === 'fr' ? 'Commentaires' : 'Comments'}</h4>
          <ReviewBlock>
            <div className="value" style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: metadata.comments }} />
          </ReviewBlock>
        </ReviewSection>
      )}

      {(!rep1.name || !rep1.email || !data.company_name) && (
        <WarningBox>
          {lang === 'fr'
            ? '⚠ Certains champs obligatoires sont manquants (nom, email, entreprise). Veuillez les compléter avant de soumettre.'
            : '⚠ Some required fields are missing (name, email, company). Please fill them in before submitting.'}
        </WarningBox>
      )}

      {alreadySubmitted && (
        <div style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '0.5rem', color: '#1E40AF', fontSize: '0.9rem' }}>
          {lang === 'fr'
            ? 'Votre inscription a déjà été soumise. Vous pouvez modifier vos informations et mettre à jour.'
            : 'Your registration has already been submitted. You can edit your information and update.'}
        </div>
      )}

      <SubmitButton
        onClick={onSubmit}
        disabled={isSubmitting || !rep1.name || !rep1.email || !data.company_name}
      >
        {isSubmitting
          ? (lang === 'fr' ? 'Envoi en cours...' : 'Submitting...')
          : alreadySubmitted
            ? (lang === 'fr' ? 'Mettre à jour mon inscription' : 'Update Registration')
            : (lang === 'fr' ? 'Soumettre mon inscription' : 'Submit Registration')}
      </SubmitButton>
    </StepContainer>
  );
};

export default SharedStep_Review;
